import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { TemplateCard } from '@backstage/plugin-scaffolder-react/alpha';

/**
 * Template card for /create. Normal templates render Backstage's own card unchanged.
 * A template annotated `sparrow.io/coming-soon: "true"` (in its template.yaml in
 * sparrow-launchpad-config) stays listed but cannot be started: no Choose button, no
 * select handler, and a "Coming soon" note. Remove the annotation to enable it again.
 */
type TemplateCardProps = Parameters<typeof TemplateCard>[0];
type TemplateEntityV1beta3 = TemplateCardProps['template'];
// What the template list really passes a custom card: the page's raw handler, which
// needs the template (TemplateCard's own onSelected takes no argument).
type CardProps = Omit<TemplateCardProps, 'onSelected'> & {
  onSelected?: (template: TemplateEntityV1beta3) => void;
};

export const COMING_SOON_ANNOTATION = 'sparrow.io/coming-soon';

export const isComingSoon = (template: TemplateEntityV1beta3) =>
  template.metadata?.annotations?.[COMING_SOON_ANNOTATION] === 'true';

/**
 * Templates shown on /create: not the Self Service request templates (tag
 * `self-request`, shown on their own pages) and not those parked as coming soon —
 * remove the annotation from a template's template.yaml to list it again.
 */
export const isListedTemplate = (template: TemplateEntityV1beta3) =>
  !(template.metadata?.tags ?? []).includes('self-request') && !isComingSoon(template);

const useStyles = makeStyles(theme => ({
  disabled: {
    position: 'relative',
    height: '100%',
    '& [data-testid="template-card-actions--create"]': { display: 'none' },
  },
  note: {
    position: 'absolute',
    right: theme.spacing(2),
    bottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));

export const SparrowTemplateCard = (props: CardProps) => {
  const classes = useStyles();
  if (!isComingSoon(props.template)) {
    // The template list passes a custom card its raw onSelected(template), but
    // TemplateCard's Choose calls onSelected() with no argument — bind the template here
    // (as Backstage does for its own card) or Choose opens nothing.
    const { onSelected, template } = props;
    return <TemplateCard {...props} onSelected={() => onSelected?.(template)} />;
  }
  return (
    <div className={classes.disabled}>
      <TemplateCard template={props.template} additionalLinks={props.additionalLinks} />
      <Typography variant="body2" className={classes.note}>
        Coming soon
      </Typography>
    </div>
  );
};
